import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseModal from './BaseModal';
import { getWebroot } from '../../utils/webroot';

interface VersionInfo {
  version: string;
  buildType: string;
  releaseDate?: string;
  buildDate?: string;
  commitHash?: string;
  branch?: string;
}

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [copyrightText, setCopyrightText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && !versionInfo) {
      setLoading(true);
      setError(null);
      
      const webroot = getWebroot();
      
      // Load version and copyright data
      const cacheBuster = Date.now();
      Promise.all([
        fetch(`${webroot}/version-info.json?v=${cacheBuster}`).then(response => {
          if (!response.ok) {
            throw new Error('Failed to load version info');
          }
          return response.json();
        }),
        fetch(`${webroot}/copyright.json?v=${cacheBuster}`).then(response => {
          if (!response.ok) {
            throw new Error('Failed to load copyright info');
          }
          return response.json();
        })
      ])
        .then(([versionData, copyrightData]) => {
          setVersionInfo(versionData);
          setCopyrightText(copyrightData.text || '');
        })
        .catch(error => {
          console.error('Failed to load about information:', error);
          // Set fallback data
          setVersionInfo({
            version: 'unknown',
            buildType: 'unknown'
          });
          setError(t('about.someInformationCouldNotBeLoaded'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, versionInfo, t]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const getBuildTypeColor = (buildType: string) => {
    switch (buildType) {
      case 'release':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'development':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('about.about')}
      size="large"
    >
      <div className="space-y-6">
        {loading && (
          <div className="text-center text-gray-500 dark:text-gray-400">
            {t('common.loading')}
          </div>
        )}

        {error && (
          <div className="text-center text-yellow-600 dark:text-yellow-400 text-sm">
            {error}
          </div>
        )}

        {versionInfo && (
          <>
            {/* Application Info */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('app.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('app.description')}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('about.poweredBy')}
              </p>
            </div>

            {/* Version Info */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="text-center">
                <div className="mb-4">
                  <span className="text-lg font-medium text-gray-700 dark:text-gray-300">
                    {t('about.version')}:
                  </span>
                  <span className="ml-3 text-xl font-mono font-bold text-gray-900 dark:text-gray-100">
                    {versionInfo.version}
                  </span>
                </div>
                
                <div className="flex justify-center mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBuildTypeColor(versionInfo.buildType)}`}>
                    {versionInfo.buildType}
                  </span>
                </div>

                {versionInfo.buildType === 'release' && versionInfo.releaseDate && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{t('about.releaseDate')}:</span>
                    <span className="ml-2">{formatDate(versionInfo.releaseDate)}</span>
                  </div>
                )}

                {versionInfo.buildType === 'development' && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {versionInfo.buildDate && (
                      <div>
                        <span className="font-medium">{t('about.buildDate')}:</span>
                        <span className="ml-2">{formatDate(versionInfo.buildDate)}</span>
                      </div>
                    )}
                    {versionInfo.branch && (
                      <div>
                        <span className="font-medium">{t('about.branch')}:</span>
                        <span className="ml-2 font-mono">{versionInfo.branch}</span>
                      </div>
                    )}
                    {versionInfo.commitHash && (
                      <div>
                        <span className="font-medium">{t('about.commit')}:</span>
                        <span className="ml-2 font-mono">{versionInfo.commitHash}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Copyright */}
            {copyrightText && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {t('about.copyright')}
                </h3>
                <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line font-mono">
                  {copyrightText}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default AboutModal;